import React from 'react'
import { css } from '@emotion/react'
import { NextPage, NextPageContext } from 'next'
import Link from 'next/link'
import { Card } from 'plume-ui'
import { A } from '@/components/atoms/A'
import { CardTitle } from '@/components/atoms/CardTitle'
import { CheckIcon } from '@/components/atoms/CheckIcon'
import { DescriptionList } from '@/components/atoms/DescriptionList'
import { Table, TableCell, TableHeading } from '@/components/atoms/Table'
import { Help } from '@/components/common/Help'
import { InvestorTemplate } from '@/components/pages/investor/InvestorTemplate'
import { DoubleColumnContent } from '@/components/pages/investor/DoubleColumnContent'
import { EmptyCard } from '@/components/common/EmptyCard'
import { useUser } from '../../../../contexts/AuthContext'
import { fetchInvestorSummary, getInvestorFund } from '@/lib/api/investor'
import { formatWithComma } from '@/lib/number'
import { isEnterpriseWithTrial } from '@/lib/user'
import { FundDetail, InvestorSummary, RoundDetail } from '../../../../types'
import Error from '../../../_error'

type Props = {
  summary: InvestorSummary | undefined
  fund: FundDetail | undefined
}

const InvestorFundPage: NextPage<Props> = (props) => {
  const user = useUser()

  if (!props.summary || !props.fund) {
    return <Error />
  }

  if (!isEnterpriseWithTrial(user)) {
    return <Error statusCode={403} />
  }

  const title = props.fund.unknown
    ? 'ファンド不明'
    : props.fund.direct
      ? '直接投資'
      : props.fund.name

  return (
    <InvestorTemplate
      summary={props.summary}
      tab="funds"
      css={css`
        position: relative;
      `}
    >
      <CardTitle>{title}</CardTitle>
      <div
        css={css`
          position: absolute;
          top: 0;
          right: 10px;
          font-size: 16px;
        `}
      >
        <Link
          href={`/funds?shortname=${props.summary.shortname}`}
          as={`/investors/${props.summary.shortname}/funds`}
        >
          <A
            href={`/investors/${props.summary.shortname}/funds`}
            decoration="none"
          >
            一覧に戻る
          </A>
        </Link>
      </div>
      {!props.fund.direct && !props.fund.unknown && (
        <Card>
          <DoubleColumnContent>
            <DescriptionList
              list={{
                '共同運用者(1)': {
                  text: props.fund.coInvestors[0] && (
                    <A
                      href={`/investors/${props.fund.coInvestors[0].shortname}`}
                      decoration="none"
                    >
                      {props.fund.coInvestors[0].name}
                    </A>
                  ),
                },
                '共同運用者(2)': {
                  text: props.fund.coInvestors[1] && (
                    <A
                      href={`/investors/${props.fund.coInvestors[1].shortname}`}
                      decoration="none"
                    >
                      {props.fund.coInvestors[1].name}
                    </A>
                  ),
                },
                ファンド種別: { text: props.fund.classification },
                URL: {
                  text: props.fund.url && (
                    <A href={props.fund.url} target="_blank">
                      {props.fund.url}
                    </A>
                  ),
                },
                'ファンド額(百万円)': {
                  text: props.fund.amount
                    ? formatWithComma(props.fund.amount)
                    : '',
                  help: '出資総額',
                },
                投資対象: {
                  text: props.fund.target,
                  help: '投資エリア、投資期間、ステージ、などファンドの特徴・ユニークな内容',
                },
                ファンド類型: {
                  text: props.fund.type,
                  help: '地域密着型、汎用型、業種特化型、産学連携型、シードアクセラレーター等',
                },
                重点業種: {
                  text: props.fund.targetIndustry,
                  help: 'ＩＴ関連・バイオ医療ヘルスケア(エネルギーを除く)・エネルギー関連・製品サービス',
                },
                重点ステージ: {
                  text: props.fund.targetStage,
                  help: 'シード・アーリー・エクスパンション・レータ・バランス型・バイアウト型・再生企業',
                },
                ファンド背景: {
                  text: props.fund.background,
                  help: 'ファンドの生れた背景・目的など',
                },
                ファンド出資者: {
                  text: props.fund.financiers,
                },
                備考: {
                  text: props.fund.remark,
                },
              }}
            />
            <DescriptionList
              list={{
                ファンドコード: { text: props.fund.shortname },
                ファンド別名旧名: { text: props.fund.aliasName },
                開始日: {
                  text: props.fund.raiseDate,
                  help: '取扱開始日、設立日、組成予定年月、（日は月末とする）',
                },
                '運用期間(年)': {
                  text: props.fund.term,
                },
                償還予定年: {
                  text: props.fund.exitDate,
                },
                '1社当たりの投資金額': {
                  text: props.fund.amountPerCompany,
                },
                '最低出資可能額(千円)': {
                  text: props.fund.minimumSizeOfInvestment,
                },
                中小企業基盤整備機構: {
                  bool: props.fund.chushouJunbi,
                  help: '中小機構の出資を示す',
                },
                産業革新機構: {
                  bool: props.fund.sangyou,
                  help: '産業革新機構の出資を示す',
                },
                ベンチャーファンド: {
                  bool: props.fund.ventureFund,
                  help: '投資対象がベンチャー企業を示す (海外特化型を除く)',
                },
              }}
            />
          </DoubleColumnContent>
        </Card>
      )}
      <div
        css={css`
          padding-top: 2rem;
        `}
      >
        <CardTitle>LP情報</CardTitle>
        {props.fund.lpDetails.length > 0 ? (
          <Card>
            <Table>
              <thead>
                <tr>
                  <TableHeading>ファンド出資者明細</TableHeading>
                  <TableHeading>分類</TableHeading>
                  <TableHeading>投資家名</TableHeading>
                  <TableHeading>LPファンド</TableHeading>
                  <TableHeading sub="百万円">出資金額</TableHeading>
                  <TableHeading>
                    ファンドオーナー
                    <Help>
                      第三者のVCをGPとし、LPとして参画している二人組合形式ファンドのLPであることを示します。
                    </Help>
                  </TableHeading>
                </tr>
              </thead>
              <tbody>
                {props.fund.lpDetails.map((lpDetail, i) => {
                  return (
                    <tr key={i}>
                      <TableCell>
                        <Link href={lpDetail.path}>
                          <A href={lpDetail.path} decoration="none">
                            {lpDetail.shortname}
                          </A>
                        </Link>
                      </TableCell>
                      <TableCell>{lpDetail.investmentType}</TableCell>
                      <TableCell>
                        <Link href={lpDetail.investor.path}>
                          <A href={lpDetail.investor.path} decoration="none">
                            {lpDetail.investor.name}
                          </A>
                        </Link>
                      </TableCell>
                      <TableCell>
                        {lpDetail.lpFund && (
                          <Link
                            href={`/investors/${lpDetail.lpFund.investorShortnames[0]}/funds/${lpDetail.lpFund.shortname}`}
                          >
                            <A
                              href={`/investors/${lpDetail.lpFund.investorShortnames[0]}/funds/${lpDetail.lpFund.shortname}`}
                              decoration="none"
                            >
                              {lpDetail.lpFund.name}
                            </A>
                          </Link>
                        )}
                      </TableCell>
                      <TableCell>
                        {lpDetail.amount && formatWithComma(lpDetail.amount)}
                      </TableCell>
                      <TableCell>{lpDetail.lpOwner && <CheckIcon />}</TableCell>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </Card>
        ) : (
          <EmptyCard />
        )}
      </div>
      <div
        css={css`
          padding-top: 2rem;
        `}
      >
        <CardTitle>ラウンド明細 (投資先)</CardTitle>
        {props.fund.roundDetails.length > 0 ? (
          <Card>
            <RoundDetailTable roundDetails={props.fund.roundDetails} />
          </Card>
        ) : (
          <EmptyCard />
        )}
      </div>
      <div
        css={css`
          padding-top: 2rem;
        `}
      >
        <CardTitle>ラウンド明細 (移動前所有者)</CardTitle>
        {props.fund.previousRoundDetails.length > 0 ? (
          <Card>
            <RoundDetailTable roundDetails={props.fund.previousRoundDetails} />
          </Card>
        ) : (
          <EmptyCard />
        )}
      </div>
    </InvestorTemplate>
  )
}

const RoundDetailTable: React.FC<{ roundDetails: RoundDetail[] }> = ({
  roundDetails,
}) => {
  return (
    <Table>
      <thead>
        <tr>
          <TableHeading>明細行</TableHeading>
          <TableHeading>年月日</TableHeading>
          <TableHeading>スタートアップ</TableHeading>
          <TableHeading>ラウンド</TableHeading>
          <TableHeading>内容</TableHeading>
          <TableHeading sub="円">株価</TableHeading>
          <TableHeading>株数</TableHeading>
          <TableHeading sub="千円">投資額</TableHeading>
        </tr>
      </thead>
      <tbody>
        {roundDetails.map((roundDetail, i) => {
          return (
            <tr key={i}>
              <TableCell>
                <A href={roundDetail.path} decoration="none">
                  {roundDetail.shortname}
                </A>
              </TableCell>
              <TableCell>{roundDetail.roundDate}</TableCell>
              <TableCell>
                <A href={roundDetail.company.path} decoration="none">
                  {roundDetail.company.name}
                </A>
              </TableCell>
              <TableCell>{roundDetail.type}</TableCell>
              <TableCell>{roundDetail.investmentDetail}</TableCell>
              <TableCell>{roundDetail.kabuka}</TableCell>
              <TableCell>{roundDetail.kabusu}</TableCell>
              <TableCell>{roundDetail.amount}</TableCell>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}

InvestorFundPage.getInitialProps = async (ctx: NextPageContext) => {
  const shortname = ctx.query.shortname!.toString()
  const fundShortname = ctx.query.fundShortname!.toString()
  const investorSummaryResponse = await fetchInvestorSummary(ctx, shortname)

  const [summary, { data: fund }] = await Promise.all([
    investorSummaryResponse?.data,
    getInvestorFund(shortname, fundShortname),
  ])

  return { summary, fund }
}

export default InvestorFundPage
